/**
 * spinachForecastCore — the PURE spinach-forecast functions, extracted out of
 * `useSpinachDashboardForecast` so they can be unit-tested and kept byte-for-byte
 * in sync with the backend port (PhenobeeV2 `forecasting/pure.py`).
 *
 * Rules for this file:
 *   - No React, no hooks, no network, no localStorage. The exported functions are
 *     pure; the one module-level side effect is dayjs plugin registration
 *     (`dayjs.extend(...)` below), required for the date parsing/formatting used here.
 *   - Behaviour must stay IDENTICAL to the hook's original inline versions.
 *     The only changes vs. the hook are: (a) `gddBaseTemperature` is now an
 *     explicit parameter instead of a closure, and (b) the React `useCallback`/
 *     `WeakMap` memoisation wrappers are dropped (they affect caching, not output).
 *
 * This module is the FRONTEND side of the shared golden-fixture parity contract.
 */
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import customParseFormat from "dayjs/plugin/customParseFormat";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { BACKEND_DATE_FORMAT, UI_DATE_FORMAT } from "../../../../consts";
import { calculateGDD } from "./utils";

dayjs.extend(isBetween);
dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat); // required for the "Do" token in UI_DATE_FORMAT

// Re-export so the canonical GDD impl lives in one place but is reachable from
// the parity surface.
export { calculateGDD };

export const filterByNameAndBlock = (name, objects) => {
  if (!objects) return [];
  const numberMatch = name.match(/\d+/);
  if (!numberMatch) return [];
  const number = parseInt(numberMatch[0], 10);

  const parseBlock = (block) => {
    if (!block) return null;
    const blockStr = block.toString().trim();
    if (blockStr.includes("-")) {
      const rangeParts = blockStr.replace(/\s+/g, "").split("-");
      if (rangeParts.length === 2) {
        const start = parseInt(rangeParts[0], 10);
        const end = parseInt(rangeParts[1], 10);
        if (!isNaN(start) && !isNaN(end)) return [start, end];
      }
      return null;
    } else {
      const num = parseInt(blockStr, 10);
      if (isNaN(num)) return null;
      return [num, num];
    }
  };

  return objects.filter((obj) => {
    const parsed = parseBlock(obj["Row"]);
    if (!parsed) return false;
    const [start, end] = parsed;
    return start <= number && number <= end;
  });
};

export const extractTableDataFromRawData = (rowName, rawData, beforeDate) => {
  if (!rawData || rawData.length === 0) return null;
  let rowData = filterByNameAndBlock(rowName, rawData);

  if (beforeDate) {
    rowData = rowData.filter((row) =>
      dayjs(row["Planting Date"]).isBefore(beforeDate),
    );
  }

  if (rowData.length === 0) return null;
  rowData.sort(
    (a, b) => new Date(a["Planting Date"]) - new Date(b["Planting Date"]),
  );
  const latestRow = rowData[rowData.length - 1];
  if (!latestRow) return null;
  return {
    bay: latestRow["Bay"],
    harvestDate: latestRow["Harvest Date"],
    plantingDate: latestRow["Planting Date"],
    block: latestRow["Row"],
    variety: latestRow["Variety"],
  };
};

export const calculateDifferenceForDateRange = (
  sortedFilteredDates,
  averageKey = "averageLength",
) => {
  if (!sortedFilteredDates || sortedFilteredDates.length < 2) return null;
  const leftmost = sortedFilteredDates[0][averageKey];
  const rightmost = sortedFilteredDates[sortedFilteredDates.length - 1][averageKey];
  return rightmost - leftmost;
};

export const percentiles = (arr, qs) => {
  if (arr.length === 0) return {};
  const sorted = arr.slice().sort((a, b) => a - b);
  const n = sorted.length;

  let result = {};
  for (let i = 0; i < qs.length; i++) {
    const q = qs[i];
    const pos = (q / 100) * (n - 1);
    const base = Math.floor(pos);
    const rest = pos - base;
    if (sorted[base + 1] !== undefined) {
      result[q] = sorted[base] + rest * (sorted[base + 1] - sorted[base]);
    } else {
      result[q] = sorted[base];
    }
  }
  return result;
};

export const getAverageFromPercentile = (lengthList, minPercent, maxPercent) => {
  const sortedList = [...lengthList].sort((a, b) => a - b);
  const minIdx = Math.floor((minPercent / 100) * sortedList.length);
  const maxIdx = Math.ceil((maxPercent / 100) * sortedList.length);
  const slice = sortedList.slice(minIdx, maxIdx);
  const sum = slice.reduce((acc, val) => acc + val, 0);
  return parseFloat((sum / (slice.length || 1)).toFixed(1));
};

/**
 * Pure version of the hook's `getSortedFilteredDatesRange` (the WeakMap memo
 * cache in the hook is omitted here — it changes caching, not the result).
 */
export const getSortedFilteredDatesRange = (
  selectedDates,
  averageData,
  excludeDates = [],
  endInclusive = true,
) => {
  if (averageData.length === 0 || selectedDates.length !== 2) return null;

  const [startDate, endDate] = selectedDates;
  const effectiveStartDate =
    startDate && dayjs(startDate).isValid()
      ? startDate
      : dayjs(averageData[0]?.date, UI_DATE_FORMAT);

  let filtered = averageData.filter(({ date }) =>
    dayjs(date, UI_DATE_FORMAT).isBetween(
      effectiveStartDate,
      endDate,
      null,
      endInclusive ? "[]" : "[)",
    ),
  );

  if (excludeDates?.length) {
    filtered = filtered.filter(({ date }) => {
      const backendFmt = dayjs(date, UI_DATE_FORMAT).format(BACKEND_DATE_FORMAT);
      return !excludeDates.includes(backendFmt);
    });
  }

  return filtered.length < 2
    ? null
    : filtered.sort((a, b) =>
        dayjs(a.date, UI_DATE_FORMAT).diff(dayjs(b.date, UI_DATE_FORMAT)),
      );
};

/**
 * Core forecast projection. `gddBaseTemperature` is an explicit parameter here
 * (it was a closure over the hook prop in the original).
 *
 * NOTE (matches frontend exactly): the emitted `averageLength` /
 * `averagePercentile80_95` are the SCALED GROWTH INCREMENT
 * (`totalGrowth * runningGDD / gddResult`), not `lastValue + increment`.
 */
export const generateEachDayForecast = (
  temperatureData,
  totalAverage,
  totalAveragePercentile80_95,
  gddResult,
  gddBaseTemperature,
) => {
  if (!temperatureData) return ["-", "-", "-"];
  let runningGDD = 0;
  const forecastArr = [];

  for (let i = 0; i < temperatureData.length; i++) {
    runningGDD += calculateGDD([temperatureData[i]], gddBaseTemperature);

    let incAvg = ((totalAverage ?? 0) * runningGDD) / (gddResult || 1);
    if (incAvg) incAvg = incAvg.toFixed(1);

    let incAvgP80_95 =
      ((totalAveragePercentile80_95 ?? 0) * runningGDD) / (gddResult || 1);
    if (incAvgP80_95) incAvgP80_95 = incAvgP80_95.toFixed(1);

    forecastArr.push({
      date: dayjs(temperatureData[i].date, BACKEND_DATE_FORMAT).format(
        UI_DATE_FORMAT,
      ),
      averageLength: parseFloat(incAvg),
      averagePercentile80_95: parseFloat(incAvgP80_95),
    });
  }
  return forecastArr;
};
